import React from "react";
import { Form, message } from "antd";
import { useState, useEffect } from "react";
import ContractProposalForm from "./ContractProposalForm";
import { useNavigate, useParams } from "react-router-dom";
import {
    addContractProposal,
    generateContractProposalPWP,
} from "../../api/ContractProposalApi";
import { updateContractProposal } from "../../api/ContractProposalApi";
import { getContractProposalById } from "../../api/ContractProposalApi";
import AdnMessageError from "../common/adnMessage/AdnMessageError";
import moment from "moment";
import * as XLSX from "xlsx";
import { GetUserByDepartmentId, getUserById } from "../../api/UserApi";
import { GetContractTypeById, getContractTypes } from "../../api/EmployeeApi";
import { getCareerLevels } from "../../api/EmployeeApi";
import { getWorkPlaces } from "../../api/WorkPlaceApi";
import { tokenKey } from "../../api/OAuth";
import { getAuthenticatedUserIdWithToken } from "../../api/ApiUtils";
import { adnNotification } from "../common/adnNotification/adnNotification";
import { AppRoutes } from "../../AppRoutes";

const ManageContractProposalPage = () => {
    const defaultContractTypeId = 1;
    const managerDepartmentId = 2;
    const academyContractId = 4;

    const [form] = Form.useForm();
    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [event, setEvent] = useState(id ? "edit" : "add");
    const [readOnly, setReadOnly] = useState(false); // Flag for financial form items
    const [fieldsUnsaved, setFieldsUnsaved] = useState(false);

    const [managers, setManagers] = useState([]);
    const [workPlaces, setWorkPlaces] = useState([]);
    const [contractTypes, setContractTypes] = useState([]);
    const [careerLevels, setCareerLevels] = useState([]);
    const [availableCareerLevels, setAvailableCareerLevels] = useState([]);

    const [excelLoading, setExcelLoading] = useState(false);
    const [excelName, setExcelName] = useState("");
    const [isExcelUploaded, setIsExcelUploaded] = useState(
        event == "edit" ? true : false
    );

    const [isEnglish, setIsEnglish] = useState(false);
    const [pptxLoading, setPptxLoading] = useState(false);
    const [submitLoading, setSubmitLoading] = useState(false);

    // Set title
    useEffect(() => {
        document.title =
            event == "edit" ? "Edit Financial Proposal" : "New Financial Proposal";
    }, [event]);

    // Setup form
    useEffect(async () => {
        await fillDefaultValues();

        if (id !== undefined) {
            await fetchContractProposalData();
        }

        await managerOptions();
        await workPlaceOptions();
        await contractTypeOptions();
        await careerLevelOptions();

        setExcelName("");
        setReadOnly(true);
        setLoading(false);
    }, []);

    // Set default values for form items
    const fillDefaultValues = async () => {
        const defaultManagerId = getAuthenticatedUserIdWithToken(
            localStorage.getItem(tokenKey)
        );
        const defaultManager = await getUserById(defaultManagerId);
        form.setFieldsValue({
            managerId: defaultManager.data.id,
            workPlaceId: defaultManager.data.workPlaceId,
        });
        const defaultContractType = await GetContractTypeById(
            defaultContractTypeId
        );
        form.setFieldsValue({
            contractTypeId: defaultContractType.data.id,
        });
    };

    // Fetch function for getting existent proposal and filling form
    const fetchContractProposalData = async () => {
        try {
            const res = await getContractProposalById(id);
            let {
                name,
                carrearLevelName,
                managerName,
                managerId,
                carrearLevelId,
                contractTypeId,
                carrearLevel,
                dayCareCheck,
                vehicleLeasing,
                capitalizationInsurance,
                technologyExpenses,
                fuelVaucher,
                startDate,
                laptopAndPhone,
                healthCare,
                salary,
                netSalary,
                yearlyNetSalary,
                expenses,
                irs,
                irsAllowance,
                cpsValuePerDay,
                lunchAllowance,
                mensalLunchAllowance = (lunchAllowance * 21).toFixed(2),
                workPlaceId,
                workPlaceName,
            } = res.data;

            if (startDate == null || startDate == NaN) {
                startDate = null;
            } else {
                startDate = moment(
                    JSON.stringify(startDate).substring(1, 11),
                    "YYYY-MM-DD"
                );
            }

            form.setFieldsValue({
                name,
                carrearLevelName,
                managerName,
                managerId,
                carrearLevelId,
                contractTypeId,
                carrearLevel,
                dayCareCheck,
                vehicleLeasing,
                capitalizationInsurance,
                technologyExpenses,
                fuelVaucher,
                startDate,
                laptopAndPhone,
                healthCare,
                salary,
                netSalary,
                yearlyNetSalary,
                expenses,
                irs,
                irsAllowance,
                cpsValuePerDay,
                lunchAllowance,
                mensalLunchAllowance,
                workPlaceId,
                workPlaceName,
            });

            setIsExcelUploaded(true);
        } catch (e) {
            navigate(AppRoutes.contractProposalList.path);
            AdnMessageError();
            return;
        }
    };

    const managerOptions = async () => {
        await GetUserByDepartmentId(managerDepartmentId).then((res) => {
            setManagers(res.data.result);
        });
    };

    const workPlaceOptions = async (value) => {
        await getWorkPlaces(value).then((res) => {
            setWorkPlaces(res.data);
        });
    };

    const contractTypeOptions = async () => {
        await getContractTypes().then((res) => {
            setContractTypes(res.data);
        });
    };

    const careerLevelOptions = async () => {
        await getCareerLevels().then((res) => {
            setCareerLevels(res.data);
            filterCareerLevelOptions(res.data);
        });
    };

    const filterCareerLevelOptions = (data = careerLevels) => {
        setAvailableCareerLevels(
            data.filter((level) => {
                if (form.getFieldValue("contractTypeId") != academyContractId)
                    return level.name !== "Trainee";
                else return level.name === "Trainee";
            })
        );
    };

    // When form values change -> register that there's unsaved data
    const handleInputChange = (values) => {
        setFieldsUnsaved(
            values.name != form.getFieldValue("name") ||
            values.originalManagerId != form.getFieldValue("managerId") ||
            values.workPlaceId != form.getFieldValue("workPlaceId") ||
            values.startDate != form.getFieldValue("startDate") ||
            values.startDate == null ||
            values.contractTypeId != form.getFieldValue("contractTypeId") ||
            values.carrearLevelId != form.getFieldValue("carrearLevelId") ||
            values.carrearLevelId == null ||
            values.carrearLevelId == undefined
        );
    };

    // Capitalize name and remove spaces
    const handleBlurName = () => {
        form.setFieldsValue({
            name: form
                .getFieldValue("name")
                ?.trim()
                .split(/\s+/)
                .filter((w) => w !== "")
                .map(
                    ([firstChar, ...rest]) =>
                        firstChar.toUpperCase() + rest.join("").toLowerCase()
                )
                .join(" "),
        });
        form.validateFields(["name"]);
    };

    // When manager changes, change workplace to correspond
    const handleManager = async (id) => {
        const res = await getUserById(id);
        form.setFieldsValue({
            workPlaceId: res.data.workPlaceId,
        });
    };

    // When contract type changes, check available levels
    const handleContractType = async (contractTypeId) => {
        if (contractTypeId !== academyContractId) {
            form.setFieldsValue({ carrearLevelId: null });
        } else {
            form.setFieldsValue({
                carrearLevelId: careerLevels.filter(
                    (level) => level.name === "Trainee"
                )[0].id,
            });
        }

        filterCareerLevelOptions();
    };

    const beforeUploadExcel = (file) => {
        const isExcel =
            file.type ===
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

        if (!isExcel) {
            message.error("You can only upload Excel files (.xlsx)!");
        }

        const isLt100kb = file.size / 1024 / 1024 < 100;

        if (!isLt100kb) {
            message.error("File must smaller than 100kb!");
        }

        return isExcel && isLt100kb;
    };

    const handleUploadExcel = (e) => {
        setExcelLoading(true);
        const file = e.file.originFileObj;

        const reader = new FileReader();

        reader.onload = (evt) => {
            const bstr = evt.target.result;

            const wb = XLSX.read(bstr, { type: "binary" });

            const wsname = wb.SheetNames[0];

            setExcelName(
                `File '${e.file.originFileObj.name}' uploaded successfully.`
            );

            const ws = wb.Sheets[wsname];

            const excelFields = {
                salary: ws.C3.v,
                netSalary: ws.B21.v,
                yearlyNetSalary: ws.O46.v,
                mensalLunchAllowance: ws.C6.v,
                lunchAllowance: ws.D6.v,
                capitalizationInsurance: ws.C10.v,
                dayCareCheck: ws.C11.v,
                vehicleLeasing: ws.C12.v,
                fuelVaucher: ws.C13.v,
                technologyExpenses: ws.C14.v,
                irs: ws.C36.v * 100,
                irsAllowance: ws.H37.v * 100,
                cpsValuePerDay: ws.F19.v,
                expenses: ws.C16.v,
                healthCare: ws.C9.v,
                laptopAndPhone: ws.C8.v,
            };

            for (const field in excelFields) {
                if (!excelFields[field] || isNaN(excelFields[field])) {
                    excelFields[field] = 0;
                } else {
                    excelFields[field] = parseFloat(excelFields[field].toFixed(2));
                }
            }

            if (wsname === "Cálculo de Projeto") {
                if (
                    excelFields.salary == 0 ||
                    excelFields.netSalary == 0 ||
                    excelFields.lunchAllowance == 0 ||
                    excelFields.yearlyNetSalary == 0
                ) {
                    setExcelName(
                        "Salary, Net Salary, LunchAllowance or Yearly Net Salary are invalid. Please upload a valid contract proposal Excel file."
                    );

                    adnNotification(
                        "Error:",
                        "Salary, Net Salary, LunchAllowance or Yearly Net Salary are invalid. Please upload a valid contract proposal Excel file."
                    );
                }
            } else {
                setExcelName(
                    "Invalid file. Was not found Sheet Name: 'Cálculo de Projeto'. "
                );

                adnNotification(
                    "Error:",
                    "Invalid file. Was not found Sheet Name: 'Cálculo de Projeto'. "
                );
            }

            form.setFieldsValue(excelFields);
            setIsExcelUploaded(true);
            setFieldsUnsaved(true);
        };
        reader.readAsBinaryString(file);

        setExcelLoading(false);
    };

    // Handle back button
    const handleCancel = () => {
        navigate(AppRoutes.contractProposalList.path);
    };

    // Form onFinnish
    const onFinish = async (values) => {
        setSubmitLoading(true);

        if (
            values.salary == 0 ||
            values.netSalary == 0 ||
            values.yearlyNetSalary == 0 ||
            values.lunchAllowance == 0
        ) {
            adnNotification(
                "Error:",
                "Salary, Net Salary, LunchAllowance or Yearly Net Salary are invalid. Please upload a valid contract proposal Excel file."
            );
        } else {
            if (event == "edit") {
                await update(values);
            } else {
                await add(values);
            }

            setFieldsUnsaved(false);
        }

        setSubmitLoading(false);
    };

    // Add proposal
    const add = async (proposal) => {
        proposal.id = 0;

        const res = await addContractProposal(proposal);
        if (res.status === 201) {
            setEvent("edit");
            message.success("Financial Proposal added");

            navigate(AppRoutes.contractProposalById.createPath(res.data.content.id));
            return;
        }

        if (res.status === 400) {
            form.setFields(
                res.data.errors.map((e) => ({
                    name: e.message.split(" ")[0].toLowerCase(),
                    errors: [e.message],
                })),
                res.data.errors.map((e) => ({
                    name:
                        e.message.split(" ")[0].toLowerCase() +
                        e.message.split(" ")[1].toUpperCase(),
                    errors: [e.message],
                }))
            );
            return;
        }
        message.error("Error adding Financial Proposal");
    };

    // Update proposal
    const update = async (proposal) => {
        proposal.id = id;
        const res = await updateContractProposal(proposal);
        if (res.status === 200) {
            message.success("Contract Proposal updated");
            return;
        }
        if (res.status === 400) {
            form.setFields(
                res.data.errors.map((e) => ({
                    name: e.message.split(" ")[0].toLowerCase(),
                    errors: [e.message],
                }))
            );
            message.error("Error updating Financial Proposal");
            return;
        }
    };

    const onLanguageSwitch = () => {
        setIsEnglish(!isEnglish);
    };

    const handleGeneratePWP = () => {
        const lang = isEnglish ? "en" : "pt";

        setPptxLoading(true);
        generateContractProposalPWP(id, lang).then((res) => {
            const url = window.URL.createObjectURL(
                new Blob([res.data], {
                    type: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
                })
            );
            let employee = form.getFieldsValue("name").name;
            let currentdate = new Date();
            let datetime =
                "_" +
                currentdate.getUTCDate() +
                (currentdate.getUTCMonth() + 1) +
                currentdate.getFullYear() +
                currentdate.getHours() +
                currentdate.getMinutes() +
                currentdate.getSeconds();
            const a = document.createElement("a");
            a.href = url;
            a.download = employee + datetime + ".pptx";
            a.click();
            window.URL.revokeObjectURL(url);
            setPptxLoading(false);
        });
    };

    // Reset page for new form
    const addNewContractProposal = async () => {
        setEvent("add");
        setIsExcelUploaded(false);
        form.setFieldsValue({
            name: "",
            startDate: null,
            carrearLevelId: null,
        });
        await fillDefaultValues();
        filterCareerLevelOptions();
        setExcelName("");
        navigate(AppRoutes.contractProposal.path);
    };

    return (
        <ContractProposalForm
            form={form}
            title={document.title}
            loading={loading}
            readOnly={readOnly}
            fieldsUnsaved={fieldsUnsaved}
            managers={managers}
            workPlaces={workPlaces}
            contractTypes={contractTypes}
            careerLevels={availableCareerLevels}
            handleInputChange={handleInputChange}
            handleBlurName={handleBlurName}
            handleManager={handleManager}
            handleContractType={handleContractType}
            beforeUploadExcel={beforeUploadExcel}
            handleUploadClick={handleUploadExcel}
            excelLoading={excelLoading}
            excelName={excelName}
            isExcelUploaded={isExcelUploaded}
            isEnglish={isEnglish}
            onLanguageSwitch={onLanguageSwitch}
            handleGeneratePWP={handleGeneratePWP}
            pptxLoading={pptxLoading}
            handleCancel={handleCancel}
            onFinish={onFinish}
            submitLoading={submitLoading}
            addNewContractProposal={addNewContractProposal}
        />
    );
};

export default ManageContractProposalPage;
